import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Star, Monitor, MapPin, ExternalLink, CalendarDays,
  ShieldCheck, Eye, Building2, Lightbulb, Compass, TrendingUp, CheckCircle2,
  Pencil, Save, Ruler, Upload, Trash2, ImageIcon, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PanelImage } from '../lib/panelImages';

// === Configuration des 3 types de panneaux ===
// Note : Reconnaissance par mots-clés du nom d'emplacement.
const LED_KEYWORDS = ['aéroport', 'aeroport', 'ledger', 'bangui mall', 'banguimall', 'mall'];
const GRAND_FORMAT_KEYWORDS = ['rond-point', 'rond point', 'république', 'republique', 'pk0', 'pk 0'];

// Mapping libellés DB → clés internes (utilisé quand le backend renvoie un type_panneau explicite).
const TYPE_DB_TO_KEY = {
  'led': 'LED',
  'déroulant': 'DEROULANT',
  'deroulant': 'DEROULANT',
  'grand format': 'GRAND_FORMAT',
  'grand_format': 'GRAND_FORMAT',
  'grandformat': 'GRAND_FORMAT',
};

function detectPanneauType(emplacement) {
  // Support legacy: si on reçoit une string, on retombe sur l'ancien comportement
  const nom = typeof emplacement === 'string' ? emplacement : (emplacement?.nom || '');
  const explicit = typeof emplacement === 'object' ? emplacement?.type_panneau : null;
  if (explicit) {
    const key = TYPE_DB_TO_KEY[explicit.toString().toLowerCase().trim()];
    if (key) return key;
  }
  const n = nom.toLowerCase();
  if (LED_KEYWORDS.some(k => n.includes(k))) return 'LED';
  if (GRAND_FORMAT_KEYWORDS.some(k => n.includes(k))) return 'GRAND_FORMAT';
  return 'DEROULANT';
}

const PANNEAU_CONFIG = {
  DEROULANT: {
    label: 'Panneau Déroulant',
    capacite: 12,            // 6 affiches × 2 côtés
    taille: '2,80 m × 1,40 m',
    description: '12 affiches qui défilent (6 de chaque côté)',
    color: 'bg-orange-100 text-orange-700 border-orange-300',
    iconColor: 'text-orange-600',
  },
  LED: {
    label: 'Panneau LED Intérieur',
    capacite: 10,
    taille: '2 m × 80 cm',
    description: '10 images animées',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    iconColor: 'text-blue-600',
  },
  GRAND_FORMAT: {
    label: 'Panneau Grand Format',
    capacite: 10,
    taille: '3,84 m × 2,40 m',
    description: '10 images',
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    iconColor: 'text-purple-600',
  },
};

// Pré-remplissage démo des sociétés par emplacement (jusqu'à la capacité)
const ENTREPRISES_PAR_EMPLACEMENT = {
  '1': [
    { name: 'ECOBANK', spaces: 4, start: '2024-03-01', end: '2025-03-01' },
    { name: 'LEDGER PLAZA', spaces: 3, start: '2024-06-01', end: '2025-06-01' },
    { name: 'ETHIOPIAN', spaces: 2, start: '2024-09-15', end: '2025-09-15' },
  ],
  '2': [
    { name: 'OMS', spaces: 6, start: '2024-01-15', end: '2025-01-15' },
    { name: 'ASKY', spaces: 4, start: '2024-04-01', end: '2025-04-01' },
  ],
  '3': [
    { name: 'ETHIOPIAN', spaces: 6, start: '2024-02-10', end: '2025-02-10' },
    { name: 'BSIC', spaces: 3, start: '2024-08-01', end: '2025-08-01' },
  ],
  '4': [
    { name: 'MINISTERE DE LA JUSTICE', spaces: 5, start: '2024-01-01', end: '2025-01-01' },
  ],
  '5': [
    { name: 'AIR FRANCE', spaces: 4, start: '2024-05-01', end: '2025-05-01' },
    { name: 'ECOBANK', spaces: 4, start: '2024-07-15', end: '2025-07-15' },
  ],
  '6': [
    { name: 'ASKY', spaces: 5, start: '2024-04-01', end: '2025-04-01' },
    { name: 'LEDGER PLAZA', spaces: 3, start: '2024-08-01', end: '2025-08-01' },
  ],
  '7': [
    { name: 'BSIC', spaces: 6, start: '2024-03-15', end: '2025-03-15' },
    { name: 'OMS', spaces: 2, start: '2024-10-01', end: '2025-10-01' },
  ],
  '8': [
    { name: 'ETHIOPIAN', spaces: 8, start: '2024-02-01', end: '2025-02-01' },
  ],
  '9': [
    { name: 'ECOBANK', spaces: 5, start: '2024-06-01', end: '2025-06-01' },
    { name: 'MINISTERE DE LA JUSTICE', spaces: 4, start: '2024-09-01', end: '2025-09-01' },
  ],
  '10': [
    { name: 'ASKY', spaces: 4, start: '2024-04-15', end: '2025-04-15' },
    { name: 'BSIC', spaces: 3, start: '2024-07-01', end: '2025-07-01' },
    { name: 'OMS', spaces: 2, start: '2024-10-15', end: '2025-10-15' },
  ],
  '11': [
    { name: 'ORANGE', spaces: 5, start: '2024-03-01', end: '2025-03-01' },
    { name: 'MTN', spaces: 4, start: '2024-06-15', end: '2025-06-15' },
  ],
  '12': [
    { name: 'TOTAL', spaces: 6, start: '2024-02-15', end: '2025-02-15' },
    { name: 'SHELL', spaces: 4, start: '2024-08-15', end: '2025-08-15' },
  ],
};

// Couleur de fond pour le logo de la société (sans charger d'images externes)
const COMPANY_PALETTE = [
  { bg: 'bg-orange-500', text: 'text-white' },
  { bg: 'bg-yellow-400', text: 'text-black' },
  { bg: 'bg-red-600', text: 'text-white' },
  { bg: 'bg-blue-700', text: 'text-white' },
  { bg: 'bg-pink-500', text: 'text-white' },
  { bg: 'bg-green-600', text: 'text-white' },
  { bg: 'bg-purple-600', text: 'text-white' },
  { bg: 'bg-cyan-600', text: 'text-white' },
];

const STATUS_BADGE = {
  'Disponible': 'bg-green-100 text-green-700 border-green-300',
  'Partiellement occupé': 'bg-orange-100 text-orange-700 border-orange-300',
  'Partiellement': 'bg-orange-100 text-orange-700 border-orange-300',
  'Occupé': 'bg-orange-100 text-orange-700 border-orange-300',
  'Indisponible': 'bg-red-100 text-red-700 border-red-300',
  'Maintenance': 'bg-yellow-100 text-yellow-700 border-yellow-300',
};

const ARRONDISSEMENT_BY_QUARTIER = {
  'Centre-ville': '1er Arrondissement',
  'PK5': '5ème Arrondissement',
  'PK12': '5ème Arrondissement',
  'Aéroport': '1er Arrondissement',
  'Boeing': '3ème Arrondissement',
  'Km5': '5ème Arrondissement',
};

export default function EmplacementDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [emplacement, setEmplacement] = useState(null);
  const [allEmplacements, setAllEmplacements] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorite, setFavorite] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ nom: '', quartier: '' });
  const [saving, setSaving] = useState(false);
  // Affiches uploadées sur le panneau
  const [affiches, setAffiches] = useState([]);
  const [showAddAffiche, setShowAddAffiche] = useState(false);
  const [newAffiche, setNewAffiche] = useState({ entreprise: '', image_data: '', filename: '' });
  const [uploadingAffiche, setUploadingAffiche] = useState(false);
  const [previewAffiche, setPreviewAffiche] = useState(null);

  // Chargement des affiches dès qu'on connaît l'emplacement
  useEffect(() => {
    if (!id) return;
    axios
      .get(`${API}/api/emplacements/${id}/affiches`)
      .then((r) => setAffiches(r.data || []))
      .catch(() => {});
  }, [API, id]);

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez choisir un fichier image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 8 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setNewAffiche((p) => ({ ...p, image_data: reader.result, filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const handleUploadAffiche = async () => {
    if (!newAffiche.image_data) {
      toast.error('Choisissez une image');
      return;
    }
    setUploadingAffiche(true);
    try {
      const res = await axios.post(`${API}/api/emplacements/${id}/affiches`, newAffiche);
      const created = {
        id: res.data?.id,
        emplacement_id: id,
        entreprise: newAffiche.entreprise,
        image_data: newAffiche.image_data,
        filename: newAffiche.filename,
        uploaded_at: new Date().toISOString(),
      };
      setAffiches((arr) => [created, ...arr]);
      toast.success('Affiche ajoutée');
      setShowAddAffiche(false);
      setNewAffiche({ entreprise: '', image_data: '', filename: '' });
    } catch {
      toast.error("Échec de l'envoi");
    } finally {
      setUploadingAffiche(false);
    }
  };

  const handleDeleteAffiche = async (affId) => {
    if (!window.confirm('Supprimer cette affiche ?')) return;
    try {
      await axios.delete(`${API}/api/emplacements/${id}/affiches/${affId}`);
      setAffiches((arr) => arr.filter((a) => a.id !== affId));
      toast.success('Affiche supprimée');
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/api/emplacements`),
          axios.get(`${API}/api/emplacement-reservations`).catch(() => ({ data: [] })),
        ]);
        setAllEmplacements(r1.data || []);
        setReservations(r2.data || []);
        const found = (r1.data || []).find((e) => e.id === id);
        setEmplacement(found || null);
        if (found) setEditForm({ nom: found.nom || '', quartier: found.quartier || '' });
      } catch {
        toast.error("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API, id]);

  const handleSaveEdit = async () => {
    if (!editForm.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/emplacements/${emplacement.id}`, {
        ...emplacement,
        nom: editForm.nom.trim(),
        quartier: editForm.quartier.trim(),
      });
      setEmplacement({ ...emplacement, nom: editForm.nom.trim(), quartier: editForm.quartier.trim() });
      toast.success('Emplacement modifié');
      setEditOpen(false);
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="emplacement">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!emplacement) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="emplacement">
        <div className="text-center py-20">
          <p className="text-gray-500 mb-4">Emplacement introuvable</p>
          <Button onClick={() => navigate('/emplacement')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" /> Retour à la liste
          </Button>
        </div>
      </Layout>
    );
  }

  // Indice (EMP-XX) basé sur l'ordre de la liste
  const idx = allEmplacements.findIndex((e) => e.id === emplacement.id);
  const code = `EMP-${String(idx + 1).padStart(2, '0')}`;
  const arrondissement = emplacement.commune || emplacement.quartier;

  // Détection du type de panneau et capacité
  const ptype = detectPanneauType(emplacement);
  const cfg = PANNEAU_CONFIG[ptype];
  const total = cfg.capacite;

  // Sociétés installées : priorité aux réservations réelles, sinon données démo
  const empReservations = reservations.filter((r) => r.emplacement_id === emplacement.id);
  let companies = [];
  if (empReservations.length > 0) {
    companies = empReservations.map((r) => ({
      name: (r.entreprise || 'Société').toUpperCase(),
      spaces: parseInt(r.nb_affiches || r.spaces || 1, 10) || 1,
      start: r.date_debut,
      end: r.date_fin,
    }));
  } else {
    companies = ENTREPRISES_PAR_EMPLACEMENT[String(idx + 1)] || [];
  }

  const occupes = companies.reduce((s, c) => s + (parseInt(c.spaces, 10) || 0), 0);
  const dispo = Math.max(0, total - occupes);
  const status = dispo === total ? 'Disponible' : dispo === 0 ? 'Indisponible' : 'Partiellement occupé';

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(emplacement.nom + ', Bangui')}`;

  return (
    <Layout user={user} onLogout={onLogout} currentPage="emplacement">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="emplacement-detail">
        {/* Header retour + favoris + modifier */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/emplacement')} className="text-gray-700 hover:text-blue-700 gap-2" data-testid="back-to-list-btn">
            <ArrowLeft className="h-4 w-4" /> Retour à la liste
          </Button>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="gap-1" data-testid="edit-emplacement-btn">
              <Pencil className="h-3.5 w-3.5" /> Modifier
            </Button>
            <button
              onClick={() => setFavorite((f) => !f)}
              className={`p-2 rounded-full transition-colors ${favorite ? 'bg-yellow-100' : 'hover:bg-gray-100'}`}
              aria-label="Favoris"
              data-testid="favorite-btn"
            >
              <Star className={`h-5 w-5 ${favorite ? 'fill-yellow-400 text-yellow-500' : 'text-gray-400'}`} />
            </button>
          </div>
        </div>

        <Card className="border-2 border-gray-100 shadow-sm">
          <CardContent className="p-6 space-y-6">
            {/* Code + Titre + Statut */}
            <div>
              <Badge className="bg-indigo-600 text-white border-0 mb-3">{code}</Badge>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl bg-gradient-to-b from-white to-slate-100 border border-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <PanelImage type={emplacement.type_panneau || { LED: 'led', GRAND_FORMAT: 'grand_format' }[ptype] || 'deroulant'} alt={cfg.label} className="h-full w-full p-1" testid="emplacement-panel-image" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">{emplacement.nom}</h1>
                    <p className="text-gray-600 text-sm">{arrondissement}, Bangui</p>
                  </div>
                </div>
                <Badge variant="outline" className={`${cfg.color} px-3 py-1 gap-1`} data-testid="status-badge">
                  <CheckCircle2 className="h-3 w-3" />
                  {status}
                </Badge>
              </div>
            </div>

            {/* 3 KPI cards : Espaces / Format / Type */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-indigo-50 rounded-lg p-4 flex items-center gap-3">
                <div className="bg-white p-2 rounded">
                  <Monitor className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="capacity-display">{dispo} / {total}</p>
                  <p className="text-xs text-gray-600">
                    Espaces disponibles<br />
                    <span className="text-gray-500">sur {total} au total</span>
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                <div className="bg-white p-2 rounded">
                  <Ruler className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900" data-testid="size-display">{cfg.taille}</p>
                  <p className="text-xs text-gray-600">Format du panneau</p>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
                <div className="bg-white p-2 rounded">
                  <Monitor className={`h-5 w-5 ${cfg.iconColor}`} />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900" data-testid="type-display">{cfg.label}</p>
                  <p className="text-xs text-gray-600">{cfg.description}</p>
                </div>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Sociétés actuellement installées */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900">
                  Sociétés actuellement installées ({occupes}/{total})
                </h2>
              </div>
              {companies.length === 0 ? (
                <p className="text-gray-400 italic text-sm">Aucune société installée pour le moment.</p>
              ) : (
                <div className="space-y-2" data-testid="companies-list">
                  {companies.map((c, i) => {
                    const palette = COMPANY_PALETTE[i % COMPANY_PALETTE.length];
                    return (
                      <div key={c.name + i} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-sm transition">
                        <div className={`${palette.bg} ${palette.text} rounded-md flex items-center justify-center h-12 w-16 font-bold text-xs shrink-0 px-1`}>
                          <span className="truncate">{c.name}</span>
                        </div>
                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                          <div>
                            <p className="text-gray-500">Société</p>
                            <p className="font-semibold text-gray-900 truncate">{c.name}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Espaces utilisés</p>
                            <p className="font-semibold text-indigo-700">{c.spaces} / {total}</p>
                          </div>
                          <div className="text-right sm:text-left">
                            <p className="text-gray-500">Période</p>
                            <p className="font-medium text-gray-700">
                              {fmtDate(c.start)} → {fmtDate(c.end)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {dispo > 0 && (
                    <div className="flex items-center gap-3 border border-dashed border-green-300 bg-green-50/50 rounded-lg p-3 text-sm">
                      <div className="bg-green-100 text-green-700 rounded-md flex items-center justify-center h-12 w-16 font-bold text-xs shrink-0">
                        +{dispo}
                      </div>
                      <p className="text-green-800 font-medium">
                        {dispo} espace{dispo > 1 ? 's' : ''} encore disponible{dispo > 1 ? 's' : ''} sur ce panneau
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* Affiches du panneau (upload + visualisation) */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-indigo-600" />
                  Affiches ({affiches.length})
                </h2>
                <Button
                  onClick={() => setShowAddAffiche(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 gap-2"
                  size="sm"
                  data-testid="add-affiche-btn"
                >
                  <Upload className="h-4 w-4" /> Ajouter une affiche
                </Button>
              </div>
              {affiches.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <ImageIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">
                    Aucune affiche pour ce panneau. Cliquez sur "Ajouter une affiche" pour téléverser une image.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="affiches-grid">
                  {affiches.map((aff) => (
                    <div key={aff.id} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-md transition">
                      <button
                        onClick={() => setPreviewAffiche(aff)}
                        className="w-full block"
                        data-testid={`affiche-${aff.id}`}
                      >
                        <img
                          src={aff.image_data}
                          alt={aff.entreprise || 'Affiche'}
                          className="w-full h-32 object-cover"
                          loading="lazy"
                        />
                      </button>
                      <div className="p-2 flex items-center justify-between gap-1">
                        <p className="text-xs font-semibold text-gray-900 truncate flex-1">
                          {aff.entreprise || aff.filename || 'Affiche'}
                        </p>
                        <button
                          onClick={() => handleDeleteAffiche(aff.id)}
                          className="text-gray-400 hover:text-red-600 transition opacity-0 group-hover:opacity-100"
                          title="Supprimer"
                          data-testid={`del-affiche-${aff.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <hr className="border-gray-200" />

            {/* Situation & géolocalisation */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-3">Situation & géolocalisation</h2>
              <div className="flex items-center gap-2 text-sm text-gray-700 mb-3">
                <MapPin className="h-4 w-4 text-indigo-600" />
                {emplacement.nom}, {arrondissement}, Bangui
              </div>
              <div className="relative bg-gradient-to-br from-blue-50 via-green-50 to-amber-50 rounded-lg h-56 overflow-hidden border border-gray-200">
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(45deg, #cbd5e1 0px, #cbd5e1 1px, transparent 1px, transparent 24px), repeating-linear-gradient(-45deg, #cbd5e1 0px, #cbd5e1 1px, transparent 1px, transparent 24px)',
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-indigo-600 text-white rounded-full p-3 shadow-lg animate-pulse">
                    <MapPin className="h-6 w-6" />
                  </div>
                </div>
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-3 right-3 bg-white border border-gray-200 px-3 py-2 rounded-md shadow text-sm text-indigo-700 hover:bg-indigo-50 flex items-center gap-1 font-medium"
                  data-testid="open-map-btn"
                >
                  Voir sur la carte <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Informations complémentaires */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-3">Informations complémentaires</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <InfoTile icon={TrendingUp} label="Trafic journalier" value="Très élevé" />
                <InfoTile icon={Eye} label="Visibilité" value="Excellente" />
                <InfoTile icon={Building2} label="Environnement" value="Urbain" />
                <InfoTile icon={Lightbulb} label="Éclairage" value={ptype === 'LED' ? 'LED Digital' : 'Oui'} />
                <InfoTile icon={Compass} label="Orientation" value={ptype === 'DEROULANT' ? 'Double face' : 'Frontal'} />
              </div>
            </div>

            {/* CTA Réservation */}
            <Button
              onClick={() => navigate(`/emplacement?reserve=${emplacement.id}`)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-base font-semibold gap-2"
              data-testid="reserve-emplacement-btn"
            >
              <CalendarDays className="h-5 w-5" />
              Réserver cet emplacement
            </Button>
            <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-2">
              <ShieldCheck className="h-3 w-3 text-green-600" /> Réservation sécurisée • Réponse sous 24h
            </p>
          </CardContent>
        </Card>

        {/* Dialog Modifier */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-md" data-testid="edit-emplacement-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-indigo-600" /> Modifier l'emplacement
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Nom de l'emplacement *</Label>
                <Input value={editForm.nom}
                       onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                       data-testid="edit-emp-nom" />
              </div>
              <div>
                <Label>Quartier / Arrondissement</Label>
                <Input value={editForm.quartier}
                       onChange={(e) => setEditForm({ ...editForm, quartier: e.target.value })}
                       data-testid="edit-emp-quartier" />
              </div>
            </div>
            <DialogFooter className="pt-4 border-t">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-emp-save">
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal upload affiche */}
        <Dialog open={showAddAffiche} onOpenChange={(o) => !uploadingAffiche && setShowAddAffiche(o)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-indigo-600" /> Ajouter une affiche
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Entreprise (optionnel)</Label>
                <Input
                  value={newAffiche.entreprise}
                  onChange={(e) => setNewAffiche({ ...newAffiche, entreprise: e.target.value })}
                  placeholder="Ex: ECOBANK"
                  data-testid="affiche-entreprise-input"
                />
              </div>
              <div>
                <Label>Image de l'affiche</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePickFile}
                  className="mt-2 block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-indigo-100 file:text-indigo-700 file:font-semibold hover:file:bg-indigo-200 cursor-pointer"
                  data-testid="affiche-file-input"
                />
                {newAffiche.image_data && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                    <img src={newAffiche.image_data} alt="Aperçu" className="max-h-56 w-full object-contain bg-gray-50" />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddAffiche(false)} disabled={uploadingAffiche}>
                Annuler
              </Button>
              <Button
                onClick={handleUploadAffiche}
                disabled={uploadingAffiche || !newAffiche.image_data}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="upload-affiche-confirm"
              >
                <Upload className="h-4 w-4 mr-1" /> {uploadingAffiche ? 'Envoi…' : 'Téléverser'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Aperçu plein écran (lightbox) */}
        <Dialog open={!!previewAffiche} onOpenChange={(o) => !o && setPreviewAffiche(null)}>
          <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black">
            <button
              onClick={() => setPreviewAffiche(null)}
              className="absolute top-3 right-3 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-1.5"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
            {previewAffiche && (
              <div className="flex flex-col items-center">
                <img
                  src={previewAffiche.image_data}
                  alt={previewAffiche.entreprise || 'Affiche'}
                  className="max-h-[80vh] w-auto object-contain"
                />
                {(previewAffiche.entreprise || previewAffiche.filename) && (
                  <div className="bg-black/60 text-white py-2 px-4 w-full text-center text-sm">
                    {previewAffiche.entreprise || previewAffiche.filename}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 flex items-center gap-2 hover:border-indigo-300 transition">
      <div className="bg-indigo-50 p-1.5 rounded">
        <Icon className="h-4 w-4 text-indigo-600" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        <p className="text-xs font-semibold text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );
}
